/**
 * 关键词库类型定义
 */

// 站点选项
export type Marketplace =
    | 'US' | 'CA' | 'MX'
    | 'UK' | 'DE' | 'FR' | 'IT' | 'ES' | 'NL' | 'SE' | 'PL' | 'BE'
    | 'JP' | 'AU' | 'SG' | 'AE' | 'SA' | 'IN' | 'BR';

// 词性分类
export type KeywordType =
    | 'core_exact'      // 核心精准
    | 'core_semi_exact' // 核心次精准
    | 'core_long_tail'  // 核心长尾
    | 'category_big'    // 品类大词
    | 'brand_big'       // 大品牌词
    | 'brand_competitor'// 竞品品牌词
    | 'brand_own'       // 自有品牌词
    | 'complementary'   // 互补词
    | 'gift'            // Gift词
    | 'promotional'     // 促销词
    | 'irrelevant'      // 不相关词
    | 'foreign'         // 外语词
    | 'misspelling';    // 错拼词

// 匹配类型
export type MatchType = 'broad' | 'phrase' | 'exact';

// 否定类型
export type NegativeType = 'keyword_exact' | 'keyword_phrase' | 'asin';

// 否定原因
export type NegativeReason =
    | 'irrelevant' | 'low_conversion' | 'high_acos'
    | 'competitor' | 'brand_protect' | 'negative_review'
    | 'wrong_category' | 'price_mismatch' | 'other';

// 否定级别
export type NegativeLevel = 'campaign' | 'ad_group' | 'account';

// 来源
export type KeywordSource =
    | 'manual' | 'aba' | 'sp_report'
    | 'competitor' | 'ai_suggest' | 'import' | 'auto_rule';

// 投放词库
export interface TargetingKeyword {
    id: number;
    organization?: number;
    marketplace: Marketplace;
    marketplace_display: string;
    sku: number;
    sku_name: string;
    keyword: string;
    keyword_type: KeywordType;
    keyword_type_display: string;
    match_type: MatchType;
    match_type_display: string;
    conversion_rate?: number;
    search_volume?: number;
    competition_level?: number;
    suggested_bid?: number;
    estimated_cpc?: number;
    current_bid?: number;
    clicks?: number;
    spend?: number;
    orders?: number;
    acos?: number;
    is_active: boolean;
    priority: number;
    source: KeywordSource;
    source_display: string;
    notes?: string;
    created_at: string;
    updated_at: string;
    created_by?: number;
}

// 否定词库
export interface NegativeKeyword {
    id: number;
    organization?: number;
    marketplace: Marketplace;
    marketplace_display: string;
    sku: number;
    sku_name: string;
    negative_type: NegativeType;
    negative_type_display: string;
    content: string;
    reason: NegativeReason;
    reason_display: string;
    level: NegativeLevel;
    level_display: string;
    campaign_name?: string;
    ad_group_name?: string;
    is_active: boolean;
    historical_clicks?: number;
    historical_spend?: number;
    historical_orders?: number;
    source: KeywordSource;
    source_display: string;
    notes?: string;
    created_at: string;
    updated_at: string;
    created_by?: number;
    is_asin_negative: boolean;
    is_keyword_negative: boolean;
}

// 关键词表现历史
export interface KeywordPerformanceHistory {
    id: number;
    targeting_keyword: number;
    keyword_text: string;
    record_date: string;
    impressions: number;
    clicks: number;
    spend: number;
    orders: number;
    sales: number;
    ctr?: number;
    cvr?: number;
    cpc?: number;
    acos?: number;
    roas?: number;
    created_at: string;
}

// 统计数据
export interface KeywordLibraryStats {
    total: number;
    active: number;
    by_keyword_type?: Record<string, number>;
    by_negative_type?: Record<string, number>;
    by_reason?: Record<string, number>;
    by_marketplace?: Record<string, number>;
    totals?: {
        total_clicks?: number;
        total_spend?: number;
        total_orders?: number;
    };
}

// 批量导入结果
export interface BulkImportResult {
    created: number;
    updated: number;
    errors: string[];
    total: number;
}

// 选项映射
export const MARKETPLACE_OPTIONS: Record<Marketplace, string> = {
    'US': '美国站',
    'CA': '加拿大站',
    'MX': '墨西哥站',
    'UK': '英国站',
    'DE': '德国站',
    'FR': '法国站',
    'IT': '意大利站',
    'ES': '西班牙站',
    'NL': '荷兰站',
    'SE': '瑞典站',
    'PL': '波兰站',
    'BE': '比利时站',
    'JP': '日本站',
    'AU': '澳大利亚站',
    'SG': '新加坡站',
    'AE': '阿联酋站',
    'SA': '沙特站',
    'IN': '印度站',
    'BR': '巴西站',
};

export const KEYWORD_TYPE_OPTIONS: Record<KeywordType, string> = {
    'core_exact': '核心精准',
    'core_semi_exact': '核心次精准',
    'core_long_tail': '核心长尾',
    'category_big': '品类大词',
    'brand_big': '大品牌词',
    'brand_competitor': '竞品品牌词',
    'brand_own': '自有品牌词',
    'complementary': '互补词',
    'gift': 'Gift词',
    'promotional': '促销词',
    'irrelevant': '不相关词',
    'foreign': '外语词',
    'misspelling': '错拼词',
};

export const MATCH_TYPE_OPTIONS: Record<MatchType, string> = {
    'broad': '广泛匹配',
    'phrase': '词组匹配',
    'exact': '精准匹配',
};

export const NEGATIVE_TYPE_OPTIONS: Record<NegativeType, string> = {
    'keyword_exact': '关键词精准否定',
    'keyword_phrase': '关键词词组否定',
    'asin': 'ASIN否定',
};

export const NEGATIVE_REASON_OPTIONS: Record<NegativeReason, string> = {
    'irrelevant': '不相关',
    'low_conversion': '低转化',
    'high_acos': '高ACoS',
    'competitor': '竞品ASIN',
    'brand_protect': '品牌保护',
    'negative_review': '差评风险',
    'wrong_category': '类目错误',
    'price_mismatch': '价格不匹配',
    'other': '其他',
};

export const NEGATIVE_LEVEL_OPTIONS: Record<NegativeLevel, string> = {
    'campaign': 'Campaign级别',
    'ad_group': 'Ad Group级别',
    'account': '账户级别',
};

export const SOURCE_OPTIONS: Record<KeywordSource, string> = {
    'manual': '手动添加',
    'aba': 'ABA数据',
    'sp_report': 'SP报告',
    'competitor': '竞品分析',
    'ai_suggest': 'AI推荐',
    'import': '批量导入',
    'auto_rule': '自动规则',
};

// 词性颜色映射
export const KEYWORD_TYPE_COLORS: Record<KeywordType, string> = {
    'core_exact': '#52c41a',
    'core_semi_exact': '#73d13d',
    'core_long_tail': '#95de64',
    'category_big': '#1890ff',
    'brand_big': '#722ed1',
    'brand_competitor': '#eb2f96',
    'brand_own': '#13c2c2',
    'complementary': '#faad14',
    'gift': '#fa541c',
    'promotional': '#f5222d',
    'irrelevant': '#8c8c8c',
    'foreign': '#bfbfbf',
    'misspelling': '#d9d9d9',
};
